import React, { useCallback, useEffect, useState} from "react";
import styles from "../../../pages/Buy/Buy.module.scss";
import timeIcon from "../../../assets/images/time.svg";
import SelectField from "../../SelectField";
import { colourStyles } from "../../form/Select/Select";
import SelectOption from "../../SelectOption/SelectOption";
import useBuyFormStore from "../../../stores/useBuyFormStore";
import useParkingInfoStore from "../../../stores/useParkingInfoStore";
import useBuyUIStore from "../../../stores/useBuyUIStore";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {useCurrentTime} from "../../../hooks/useCurrentTime";
import {ParkingOptionsService} from "../../../services/ParkingOptionsService";
import { useGetParkingPriceByMinutes } from "hooks/api/useGetParkingPriceByMinutes";

interface TimeSelectionProps {
    isPriceLoading: boolean;
}

const TimeSelection: React.FC<TimeSelectionProps> = ({ isPriceLoading }) => {
    const { t } = useTranslation();
    const { nowDate } = useCurrentTime({ timeShiftMs: 5000 });

    const formStore = useBuyFormStore();
    const parkingStore = useParkingInfoStore();
    const uiStore = useBuyUIStore();

    const {
        data: priceByMinutesData,
        isLoading: isPriceByMinutesLoading,
        isError: isPriceByMinutesError,
        error: priceByMinutesError,
    } = useGetParkingPriceByMinutes({
        licensePlate: formStore.licencePlate,
        featureType: parkingStore.featureType,
        featureCode: parkingStore.sectionCode,
        requestTime: nowDate.toISO(),
    });

    const [isDisabled, setIsTimeDisabled] = useState<boolean>(false);
    
    const parkingOptionsService = new ParkingOptionsService(t);

    useEffect(() => {
        if (!parkingStore.sectionCode || !formStore.licencePlate || formStore.licencePlate.length === 0) {
            uiStore.setDisablePaymentButtonsDueToLimitExceeded(false);
            setIsTimeDisabled(true);
            return;
        }

        const fetchCalculations = async () => {
            if (isPriceByMinutesLoading) return;
            
            try {
                const timeOptions = await parkingOptionsService.getParkingTimeOptions(
                    priceByMinutesData, 
                    parkingStore.parkingZone?.tariffInfo?.minPrice || parkingStore.parkingMachine?.tariffInfo?.minPrice || 0
                );
                parkingStore.setCurrOptions(timeOptions);

                if (timeOptions.length > 0) {
                    setIsTimeDisabled(false);
                    uiStore.setDisablePaymentButtonsDueToLimitExceeded(false);
                } else {
                    uiStore.setDisablePaymentButtonsDueToLimitExceeded(true);
                    setIsTimeDisabled(true);
                }

                if (formStore.requestedMinutes && timeOptions.length > 0) {
                    const selectedOption = timeOptions.find((o) => 
                        Number(o.value) === Number(formStore.requestedMinutes?.value)
                    ) || timeOptions.find((o) =>
                        o.optionHeaderText === formStore.requestedMinutes?.optionHeaderText
                    );		

                    if (selectedOption) {
                        formStore.setRequestedMinutes({
                            ...formStore.requestedMinutes,
                            value: selectedOption.value,
                            optionHeaderText: selectedOption.optionHeaderText,
                            isDisabled: selectedOption.isDisabled,
                            label: selectedOption.label,
                            labelString: selectedOption.labelString,
                            endOfParking: selectedOption.endOfParking,
                        });

                        return;
                    }

                    const fallback = timeOptions[0];
                    formStore.setRequestedMinutes({
                        isDisabled: fallback.isDisabled,
                        label: fallback.label,
                        labelString: fallback.labelString,
                        value: fallback.value,
                        optionHeaderText: fallback.optionHeaderText,
                        endOfParking: fallback.endOfParking,
                    });
                }
            } catch (e) {
                console.error("Could not generate parking options", e);
                uiStore.setDisablePaymentButtonsDueToLimitExceeded(true);
                setIsTimeDisabled(true);
            }
        }

        fetchCalculations();
    }, [
        priceByMinutesData,
        parkingStore.parkingZone,
        parkingStore.sectionCode,
        parkingStore.currentSession,
        formStore.licencePlate,
        nowDate,
        t,
    ]);

    useEffect(() => {
        if (!formStore.requestedMinutes?.value) return;

        const newToDate = nowDate.plus({ minutes: Number(formStore.requestedMinutes.value) });
        parkingStore.setToDate(newToDate);
    }, [nowDate, formStore.requestedMinutes?.value]);

    const getPlaceholder = () => {
        if (!formStore.licencePlate) {
            return t("buy.carPlaceholder");
        }
        return isDisabled ? "" : undefined;
    }

    const formatDate = useCallback((date: DateTime) => {
        return date.toFormat("dd. MM. HH:mm");
    }, []);

    const displayToDate = formStore.requestedMinutes?.endOfParking
        ? DateTime.fromISO(formStore.requestedMinutes.endOfParking)
        : parkingStore.toDate;

    const toDateClass = () => {
        if (isPriceLoading) return styles.textMuted;
        return parkingStore.toDate.hasSame(nowDate, "day") ? styles.textMuted : styles.textDanger;
    }

    return (
        <div className={styles.item}>
            <div className={styles.info}>
                <div className={styles.icon}>
                    <img alt="Time" src={timeIcon} />
                </div>
                <div className={styles.timeInfo}>
                    <div className={`${styles.title} ${styles.titleBuy}`}>
                        <div className={`${styles.itemTitle} ${styles.itemTitleBuy}`}>{t("buy.time")}</div>
                    </div>
                    <div className={toDateClass()}>
                        { !isDisabled && !isPriceLoading && `${t("buy.toDate")} ${formatDate(displayToDate)}` }
                    </div>
                </div>
                <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax} ${styles.selectTime}`}>
                    <SelectField
                        name="requestedMinutes"
                        onSelectionChange={(value) => {
                            formStore.setRequestedMinutes(value);
                            uiStore.setIsSpecialOption(!!value?.isSpecial);
                        }}
                        getOptionValue={(option) => option.value}
                        getOptionLabel={(option) => option.label}
                        options={!isDisabled ? parkingStore.currOptions : []}
                        styles={colourStyles}
                        placeholder={getPlaceholder()}
                        isSearchable={false}
                        isDisabled={isDisabled}
                        components={{
                            IndicatorSeparator: () => null,
                            Option: SelectOption,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TimeSelection;
